import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, TrendingUp } from "lucide-react";
import { Eyebrow, Phone, StatusBar, Avatar, Chip, Parallax } from "../components/bits";

const ease = [0.22, 1, 0.36, 1];
const STEP_MS = 2600;
const TOTAL = 9;

const STEPS = [
    "Client profile identified",
    "Timeline moves forward",
    "Return window reached",
    "Personalised SMS sent",
    "Returning-client offer",
    "Client replies",
    "Calendar opens",
    "Appointment confirmed",
    "Revenue recovered",
];

const ScenePhone = ({ step }) => (
    <Phone className="w-[270px] sm:w-[300px]">
        <StatusBar />
        <div className="relative flex h-[540px] flex-col px-4 pb-4 pt-1">
            <div className="flex items-center gap-2.5 border-b border-linen pb-2.5">
                <Avatar initials="R" tone="bg-rose text-white ring-2 ring-rose/30" />
                <div className="flex-1">
                    <div className="font-sans text-[11px] font-medium text-ink">Rose Clinic 🤍</div>
                    <div className="font-mono text-[7px] uppercase tracking-[0.18em] text-rose">
                        Reactivation system · Live
                    </div>
                </div>
                <span className="h-1.5 w-1.5 animate-pulse-soft rounded-full bg-rose" />
            </div>

            <AnimatePresence>
                {step <= 3 && (
                    <motion.div
                        key="profile"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.5, ease }}
                        className="mt-3 overflow-hidden rounded-xl border border-linen bg-ivory p-3"
                    >
                        <div className="flex items-center gap-2.5">
                            <Avatar initials="S" tone="bg-blush text-ink" />
                            <div>
                                <div className="font-serif text-base leading-none text-ink">Sarah</div>
                                <div className="mt-1 font-mono text-[7px] uppercase tracking-[0.16em] text-faint">
                                    Lip filler · Last visit: 12 months ago
                                </div>
                            </div>
                            {step >= 2 && (
                                <motion.span
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="ml-auto rounded-full bg-rose/25 px-2 py-1 font-mono text-[7px] uppercase tracking-[0.14em] text-ink"
                                >
                                    Return window
                                </motion.span>
                            )}
                        </div>
                        <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-sand">
                            <motion.div
                                className="h-full rounded-full bg-gradient-to-r from-rose to-gold"
                                initial={{ width: "12%" }}
                                animate={{ width: step >= 2 ? "100%" : step >= 1 ? "72%" : "12%" }}
                                transition={{ duration: 1.1, ease }}
                            />
                        </div>
                        <div className="mt-1.5 flex justify-between font-mono text-[7px] uppercase tracking-[0.14em] text-faint">
                            <span>Visit</span>
                            <span className={step >= 2 ? "text-gold" : ""}>
                                {step >= 2 ? "Due now" : "11 mo → 12 mo"}
                            </span>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="mt-3 flex flex-1 flex-col gap-2 overflow-hidden">
                <AnimatePresence>
                    {step >= 3 && (
                        <motion.div
                            key="sms"
                            initial={{ opacity: 0, x: -18 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.55, ease }}
                            className="max-w-[92%] rounded-2xl rounded-tl-sm bg-white p-3 shadow-[0_6px_18px_rgba(17,17,19,0.06)]"
                        >
                            <p className="font-sans text-[9.5px] leading-relaxed text-ink">
                                Hi Sarah, it’s the team at Rose Clinic. It’s been almost a year since your
                                last lip filler appointment with us — we usually see clients around this time for
                                their next refresh, so we thought we’d check in. If you’ve been thinking about
                                another treatment, we’d love to see you again.
                            </p>
                            {step >= 4 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, ease }}
                                    className="mt-2 rounded-lg border border-rose/60 bg-blush px-2.5 py-2 text-center"
                                >
                                    <div className="font-mono text-[7px] uppercase tracking-[0.2em] text-rose">
                                        Returning-client offer
                                    </div>
                                    <div className="mt-0.5 font-serif text-sm text-ink">
                                        10% off your next lip filler appointment
                                    </div>
                                </motion.div>
                            )}
                            <div className="mt-1.5 font-mono text-[7px] text-faint">Would you like us to help you find a time?</div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {step >= 5 && (
                        <motion.div
                            key="reply"
                            initial={{ opacity: 0, x: 18 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.55, ease }}
                            className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-ink p-3"
                        >
                            <div className="font-mono text-[7px] uppercase tracking-[0.16em] text-gold">Sarah</div>
                            <p className="mt-1 font-sans text-[9.5px] leading-relaxed text-cream">
                                Yes please 🤍 I’ve actually been thinking about getting them done again. Do you
                                have anything next week?
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {step >= 6 && (
                        <motion.div
                            key="cal"
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.55, ease }}
                            className="rounded-xl border border-linen bg-ivory p-3"
                        >
                            <div className="grid grid-cols-7 gap-1">
                                {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                                    <motion.span
                                        key={i}
                                        animate={
                                            step >= 7 && i === 3
                                                ? { backgroundColor: "#111113", color: "#FAF8F5" }
                                                : {}
                                        }
                                        className={`flex aspect-square items-center justify-center rounded font-mono text-[8px] ${
                                            i === 3 ? "text-ink ring-1 ring-gold" : "bg-white text-ink/40"
                                        }`}
                                    >
                                        {d}
                                    </motion.span>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {step >= 7 && (
                        <motion.div
                            key="confirm"
                            initial={{ opacity: 0, scale: 0.92 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5, ease }}
                            className="flex items-center gap-2.5 rounded-xl bg-ink p-3"
                        >
                            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gold/15">
                                <Check className="h-3.5 w-3.5 text-gold" strokeWidth={2.5} />
                            </span>
                            <div>
                                <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-gold">
                                    Appointment confirmed ✓
                                </div>
                                <div className="mt-0.5 font-serif text-base leading-none text-cream">
                                    Thursday · 2:30 PM
                                </div>
                            </div>
                            {step >= 8 && (
                                <motion.span
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="ml-auto flex items-center gap-1 font-mono text-[8px] uppercase tracking-widest text-gold"
                                >
                                    <TrendingUp className="h-3 w-3" /> ↑
                                </motion.span>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    </Phone>
);

export const PhoneScene = () => {
    const [step, setStep] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setStep((s) => (s + 1) % TOTAL), STEP_MS);
        return () => clearInterval(t);
    }, []);

    return (
        <section data-testid="phone-scene" className="grain relative overflow-hidden bg-obsidian py-20 lg:py-24">
            <div className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full bg-rose/10 blur-3xl" />
            <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-[1fr_auto] lg:gap-20 lg:px-8">
                <div>
                    <Eyebrow dark>The system in motion</Eyebrow>
                    <h2 className="mt-4 font-serif text-2xl uppercase leading-[1.12] tracking-tight text-cream sm:text-3xl lg:text-4xl">
                        Client reactivation for cosmetic &amp; aesthetic clinics
                    </h2>
                    <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#B9B6C0] sm:text-base">
                        Watch one past client move from overdue to booked. The right client, the right message,
                        the right time.
                    </p>
                    <ol className="mt-9 space-y-0" data-testid="scene-steps">
                        {STEPS.map((label, i) => (
                            <li key={label} className="flex items-center gap-4 border-b border-cream/8 py-2.5">
                                <span
                                    className={`font-mono text-[10px] tracking-[0.2em] transition-colors duration-500 ${
                                        i === step ? "text-gold" : "text-cream/30"
                                    }`}
                                >
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span
                                    className={`font-sans text-[13px] transition-colors duration-500 ${
                                        i === step ? "text-cream" : i < step ? "text-cream/55" : "text-cream/30"
                                    }`}
                                >
                                    {label}
                                </span>
                                {i === step && (
                                    <motion.span
                                        layoutId="step-dot"
                                        className="ml-auto h-1.5 w-1.5 rounded-full bg-rose"
                                    />
                                )}
                            </li>
                        ))}
                    </ol>
                </div>

                <div className="relative mx-auto">
                    <Parallax speed={26}>
                    <div className="animate-float-soft">
                        <ScenePhone step={step} />
                    </div>
                    </Parallax>
                    <Chip dark className="absolute -left-14 top-16 hidden animate-float md:inline-flex">12 months</Chip>
                    <Chip dark className="absolute -right-16 top-36 hidden animate-float-soft md:inline-flex">10% offer</Chip>
                    <Chip dark className="absolute -left-16 bottom-44 hidden animate-float md:inline-flex">Reactivated</Chip>
                    <Chip dark className="absolute -right-12 bottom-24 hidden animate-float-soft md:inline-flex">
                        Booked <Check className="h-3 w-3 text-gold" strokeWidth={2.5} />
                    </Chip>
                    <Chip dark className="absolute -bottom-5 left-1/2 hidden -translate-x-1/2 animate-float md:inline-flex">
                        Revenue <TrendingUp className="h-3 w-3 text-gold" />
                    </Chip>
                </div>
            </div>
        </section>
    );
};
